const MAX_AVATAR_EDGE_PX = 1024
const TARGET_MIME = 'image/webp'
const FALLBACK_MIME = 'image/jpeg'
const TARGET_QUALITY = 0.85

const loadImageElement = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to decode avatar image'))
    }
    img.src = url
  })
}

const canvasToBlob = (canvas: HTMLCanvasElement, mime: string, quality: number): Promise<Blob | null> => {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mime, quality)
  })
}

/**
 * Downscales and recompresses avatar uploads (target ~100–200 KB).
 * Returns the original file when compression is unnecessary or unsupported.
 */
export const compressAvatarFile = async (file: File): Promise<File> => {
  if (!file.type.startsWith('image/') || file.type.includes('svg')) {
    return file
  }

  try {
    const image = await loadImageElement(file)
    const longestEdge = Math.max(image.naturalWidth, image.naturalHeight)
    const scale = longestEdge > MAX_AVATAR_EDGE_PX
      ? MAX_AVATAR_EDGE_PX / longestEdge
      : 1

    // Skip re-encode for already-small images under the edge limit.
    if (scale === 1 && file.size <= 200_000) {
      return file
    }

    const width = Math.max(1, Math.round(image.naturalWidth * scale))
    const height = Math.max(1, Math.round(image.naturalHeight * scale))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return file

    ctx.drawImage(image, 0, 0, width, height)

    let blob = await canvasToBlob(canvas, TARGET_MIME, TARGET_QUALITY)
    let mime = TARGET_MIME
    if (!blob) {
      blob = await canvasToBlob(canvas, FALLBACK_MIME, TARGET_QUALITY)
      mime = FALLBACK_MIME
    }
    if (!blob || blob.size >= file.size) {
      return file
    }

    const extension = mime === TARGET_MIME ? 'webp' : 'jpg'
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'avatar'
    return new File([blob], `${baseName}.${extension}`, { type: mime, lastModified: Date.now() })
  } catch (e) {
    console.error(e)
    return file
  }
}
